import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Activity Log Modal - Enterprise-grade activity tracking
 * 
 * Shows full chronological history of all actions on a submission:
 * - Emails sent (digests, clarifications, hot leads)
 * - Admin edits (field changes, approvals, rejections)
 * - Sales updates (status changes, value updates)
 * - AI actions (auto-approvals, reruns)
 * - System actions (branch assignments, geocoding)
 * 
 * Features:
 * - Chronological view (newest first)
 * - Group by type filtering
 * - Color-coded activity types
 * - Expandable details
 */
const ActivityLogModal = ({ isOpen, inspection, backendUrl, onClose }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Activity type metadata (icons, colors, labels)
  const activityMeta = {
    submission_created: { icon: '📝', color: 'blue', label: 'Created' },
    email_sent: { icon: '✉️', color: 'green', label: 'Email Sent' },
    email_received: { icon: '📬', color: 'purple', label: 'Reply Received' },
    admin_edit: { icon: '✏️', color: 'yellow', label: 'Admin Edit' },
    approval: { icon: '✅', color: 'green', label: 'Approved' },
    auto_approved: { icon: '🤖', color: 'blue', label: 'Auto-Approved' },
    rejection: { icon: '❌', color: 'red', label: 'Rejected' },
    status_change: { icon: '🔄', color: 'blue', label: 'Status Changed' },
    sales_update: { icon: '💰', color: 'green', label: 'Sales Update' },
    branch_assigned: { icon: '🏢', color: 'blue', label: 'Branch Assigned' },
    ai_rerun: { icon: '🔄', color: 'purple', label: 'AI Rerun' },
    geocoded: { icon: '📍', color: 'blue', label: 'Geocoded' },
    deleted: { icon: '🗑️', color: 'red', label: 'Deleted' },
    archived: { icon: '📦', color: 'gray', label: 'Archived' },
    routed_to_admin: { icon: '🚨', color: 'yellow', label: 'Flagged for Review' }
  };

  useEffect(() => {
    if (isOpen && inspection) {
      fetchActivityLog();
    }
  }, [isOpen, inspection]);

  const fetchActivityLog = () => {
    if (!inspection?.activity_log) {
      setActivities([]);
      return;
    }

    // Use activity_log from inspection (already sorted newest first during backfill)
    const sortedActivities = [...inspection.activity_log].sort((a, b) => {
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

    setActivities(sortedActivities);
  };

  const toggleExpand = (index) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const filteredActivities = filterType === 'all'
    ? activities
    : activities.filter(a => a.type === filterType);

  // Get unique activity types for filter buttons
  const activityTypes = [...new Set(activities.map(a => a.type))];

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timestamp;
    }
  };

  const renderActivityDetails = (activity) => {
    const details = activity.details || {};
    const entries = Object.entries(details).filter(([key, value]) => value != null && value !== '');

    if (entries.length === 0) return null;

    return (
      <div className="mt-2 pl-8 space-y-1">
        {entries.map(([key, value]) => (
          <div key={key} className="text-xs text-white/60 flex gap-2">
            <span className="font-semibold capitalize">{key.replace(/_/g, ' ')}:</span>
            <span className="text-white/80">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
          </div>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  const modal = (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4 pt-20 pb-6 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-[var(--pbfs-navy)] rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col border-2 border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              📋 Activity Log
              <span className="text-sm font-normal text-white/50">
                ({filteredActivities.length} {filterType === 'all' ? 'total' : 'filtered'})
              </span>
            </h2>
            <p className="text-white/60 text-sm mt-1">
              {inspection?.business_name || inspection?.location || 'Submission'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white text-2xl leading-none transition-colors"
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Filter Buttons */}
        <div className="p-4 border-b border-white/10 flex flex-wrap gap-2">
          <button
            onClick={() => setFilterType('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filterType === 'all'
                ? 'bg-[var(--pbfs-blue)] text-white'
                : 'bg-white/10 text-white/60 hover:bg-white/20'
            }`}
          >
            All ({activities.length})
          </button>
          {activityTypes.map(type => {
            const meta = activityMeta[type] || { icon: '•', label: type };
            const count = activities.filter(a => a.type === type).length;
            return (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  filterType === type
                    ? 'bg-[var(--pbfs-blue)] text-white'
                    : 'bg-white/10 text-white/60 hover:bg-white/20'
                }`}
              >
                {meta.icon} {meta.label} ({count})
              </button>
            );
          })}
        </div>

        {/* Activity List */}
        <div className="flex-1 overflow-y-auto p-6">
          {filteredActivities.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📭</div>
              <p className="text-white/60">No activity logged yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredActivities.map((activity, index) => {
                const meta = activityMeta[activity.type] || { icon: '•', color: 'gray', label: activity.type };
                const isExpanded = expandedItems.has(index);
                const hasDetails = activity.details && Object.keys(activity.details).length > 0;

                return (
                  <div
                    key={index}
                    className={`bg-white/5 rounded-lg p-4 hover:bg-white/10 transition-all border border-white/10 ${
                      hasDetails ? 'cursor-pointer' : ''
                    }`}
                    onClick={() => hasDetails && toggleExpand(index)}
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className="text-2xl flex-shrink-0">
                        {meta.icon}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="text-white font-semibold text-sm">
                              {activity.action}
                            </div>
                            <div className="text-white/50 text-xs mt-1">
                              {activity.actor} • {formatTimestamp(activity.timestamp)}
                            </div>
                          </div>

                          {/* Type Badge */}
                          <span className={`px-2 py-1 rounded text-[10px] font-semibold uppercase tracking-wider bg-${meta.color}-500/20 text-${meta.color}-400 border border-${meta.color}-500/40 flex-shrink-0`}>
                            {meta.label}
                          </span>
                        </div>

                        {/* Expandable Details */}
                        {hasDetails && isExpanded && renderActivityDetails(activity)}
                        
                        {hasDetails && (
                          <div className="text-white/40 text-xs mt-2">
                            {isExpanded ? '▼ Click to collapse' : '▶ Click to expand details'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-[var(--pbfs-blue)] text-white rounded-lg hover:bg-[var(--pbfs-blue)]/80 transition-colors font-semibold"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ActivityLogModal;

